import { useEffect, useRef, useState } from 'react';

import { orgsApi, SlugCheck } from '@/features/org/api/orgs.api';

/**
 * Debounced slug availability check. Used by CreateOrgModal +
 * OrgSettingsPage to gate the submit button on real-time backend
 * feedback so the user never fills a form and submits into a 409.
 *
 * Behavior:
 *   - Empty input → status: 'idle' (no message; submit disabled).
 *   - User typing → status: 'checking' after 300ms debounce, no request
 *     in flight earlier than that.
 *   - On request landing → 'available' or 'unavailable' with `message`.
 *   - The hook is race-safe: a new keystroke aborts an in-flight check
 *     so we never let an old response stamp a stale answer on the UI.
 *
 * `excludeOrgId` is for the rename form — passes the org's own id so
 * its current slug doesn't show as taken-by-itself.
 */

export interface SlugCheckState {
  status: 'idle' | 'checking' | 'available' | 'unavailable';
  message: string | null;
  reason: SlugCheck['reason'];
}

export function useSlugCheck(rawSlug: string, excludeOrgId?: string): SlugCheckState {
  const [state, setState] = useState<SlugCheckState>({
    status: 'idle',
    message: null,
    reason: undefined,
  });
  const seqRef = useRef(0);
  const slug = (rawSlug || '').toLowerCase().trim();

  useEffect(() => {
    if (!slug) {
      setState({ status: 'idle', message: null, reason: undefined });
      return undefined;
    }
    setState({ status: 'checking', message: null, reason: undefined });

    const seq = ++seqRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await orgsApi.checkSlug(slug, excludeOrgId);
        // Drop stale responses (user kept typing after this fetch fired).
        if (seq !== seqRef.current) return;
        setState({
          status: res.available ? 'available' : 'unavailable',
          message: res.message ?? null,
          reason: res.reason,
        });
      } catch (err: any) {
        if (seq !== seqRef.current) return;
        setState({
          status: 'unavailable',
          message: err?.message || 'Check failed',
          reason: undefined,
        });
      }
    }, 300);

    return () => clearTimeout(t);
  }, [slug, excludeOrgId]);

  return state;
}
